import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

import { uploadsApi } from 'lib/api'

interface BatchDetails {
  total: number
  processed: number
  successful: number
  failed: number
  skipped: number
}

interface CVProcessingStatus {
  isProcessing: boolean
  processingCount: number
  lastProcessedFile?: string
  statusMessage?: string
  lastError?: string | null
  batchDetails?: BatchDetails
  activeBatches?: Map<string, BatchDetails> // Track multiple batches by ID
}

interface CVProcessingContextType {
  cvProcessing: CVProcessingStatus
  startProcessing: (fileName: string) => void
  finishProcessing: (options?: { showDoneMessage?: boolean }) => void
  setProcessingCount: (count: number) => void
  reportError: (message: string) => void
  setStatusMessage: (message: string, batchDetails?: BatchDetails, batchId?: string) => void
  removeBatch: (batchId: string) => void
}

const CVProcessingContext = createContext<CVProcessingContextType | undefined>(undefined)

const STORAGE_KEY = 'cv_processing_status'

export function CVProcessingProvider({ children }: { children: ReactNode }) {
  const [cvProcessing, setCvProcessing] = useState<CVProcessingStatus>(() => {
    // Initialize from localStorage on first load
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem(STORAGE_KEY)
        if (saved) {
          const parsed = JSON.parse(saved)
          return {
            isProcessing: parsed.isProcessing || false,
            processingCount: parsed.processingCount || 0,
            lastProcessedFile: parsed.lastProcessedFile,
            statusMessage: parsed.statusMessage,
            lastError: parsed.lastError ?? null,
            batchDetails: parsed.batchDetails,
            activeBatches: parsed.activeBatches ? new Map(Object.entries(parsed.activeBatches)) : undefined
          }
        }
      } catch (error) {
        console.error('Error loading CV processing status from localStorage:', error)
      }
    }
    return {
      isProcessing: false,
      processingCount: 0,
      batchDetails: undefined
    }
  })

  const startProcessing = (fileName: string) => {
    console.log('🔄 [CVProcessing] Starting processing for file:', fileName)
    setCvProcessing(prev => {
      const newState = {
        isProcessing: true,
        processingCount: prev.processingCount + 1,
        lastProcessedFile: fileName,
        statusMessage: `Extracting details from ${fileName}…`,
        lastError: null, // Clear any previous errors when starting new processing
        batchDetails: prev.batchDetails, // Keep batch details if they exist
        activeBatches: prev.activeBatches // Keep active batches if they exist
      }
      console.log('🔄 [CVProcessing] New state:', newState)
      return newState
    })
    // Auto-finish processing after a reasonable time (CV processing typically takes 10-30 seconds)
    console.log('⏰ [CVProcessing] Setting 30-second timeout for auto-finish')
    setTimeout(() => {
      console.log('⏰ [CVProcessing] 30-second timeout triggered')
      setCvProcessing(current => {
        if (current.processingCount > 0) {
          const newState = {
            isProcessing: current.processingCount > 1,
            processingCount: Math.max(0, current.processingCount - 1),
            lastProcessedFile: current.processingCount <= 1 ? undefined : current.lastProcessedFile,
            statusMessage: current.processingCount <= 1 ? 'Processing complete' : current.statusMessage,
            lastError: current.processingCount <= 1 ? null : current.lastError
          }
          console.log('⏰ [CVProcessing] Timeout auto-finish new state:', newState)
          return newState
        }
        return current
      })
    }, 30000) // 30 seconds timeout
  }

  const finishProcessing = (options?: { showDoneMessage?: boolean }) => {
    console.log('✅ [CVProcessing] Finishing processing')
    setCvProcessing(prev => {
      const newState = {
        isProcessing: prev.processingCount > 1,
        processingCount: Math.max(0, prev.processingCount - 1),
        lastProcessedFile: prev.processingCount <= 1 ? undefined : prev.lastProcessedFile,
        statusMessage: options?.showDoneMessage && prev.processingCount <= 1 ? 'Processing completed' : prev.statusMessage,
        lastError: prev.processingCount <= 1 ? null : prev.lastError,
        batchDetails: prev.processingCount <= 1 ? undefined : prev.batchDetails
      }
      console.log('✅ [CVProcessing] Finish processing new state:', newState)
      return newState
    })
  }

  const setProcessingCount = (count: number) => {
    setCvProcessing(prev => ({
      isProcessing: count > 0,
      processingCount: count,
      lastProcessedFile: count > 0 ? prev.lastProcessedFile : undefined,
      statusMessage: count > 0 ? prev.statusMessage : 'Processing complete',
      lastError: count > 0 ? prev.lastError : null,
      batchDetails: count > 0 ? prev.batchDetails : undefined
    }))
  }

  const reportError = (message: string) => {
    setCvProcessing(prev => ({
      ...prev,
      isProcessing: false,
      processingCount: 0,
      lastError: message,
      statusMessage: undefined,
      lastProcessedFile: undefined,
      batchDetails: undefined
    }))
  }

  const setStatusMessage = (message: string, batchDetails?: BatchDetails, batchId?: string) => {
    setCvProcessing(prev => {
      const newActiveBatches = new Map(prev.activeBatches || new Map())
      
      // If batchId is provided, update that specific batch
      if (batchId && batchDetails) {
        newActiveBatches.set(batchId, batchDetails)
      } else if (batchDetails) {
        // If no batchId, use a default key (for backward compatibility)
        newActiveBatches.set('default', batchDetails)
      }
      
      // Aggregate all batch details
      let aggregatedDetails: BatchDetails | undefined
      if (newActiveBatches.size > 0) {
        aggregatedDetails = {
          total: 0,
          processed: 0,
          successful: 0,
          failed: 0,
          skipped: 0
        }
        newActiveBatches.forEach((details) => {
          aggregatedDetails!.total += details.total
          aggregatedDetails!.processed += details.processed
          aggregatedDetails!.successful += details.successful
          aggregatedDetails!.failed += details.failed
          aggregatedDetails!.skipped += details.skipped
        })
      }
      
      return {
        ...prev,
        statusMessage: message,
        batchDetails: aggregatedDetails,
        activeBatches: newActiveBatches
      }
    })
  }

  const removeBatch = (batchId: string) => {
    setCvProcessing(prev => {
      const newActiveBatches = new Map(prev.activeBatches || new Map())
      newActiveBatches.delete(batchId)
      
      // Aggregate remaining batch details
      let aggregatedDetails: BatchDetails | undefined
      if (newActiveBatches.size > 0) {
        aggregatedDetails = {
          total: 0,
          processed: 0,
          successful: 0,
          failed: 0,
          skipped: 0
        }
        newActiveBatches.forEach((details) => {
          aggregatedDetails!.total += details.total
          aggregatedDetails!.processed += details.processed
          aggregatedDetails!.successful += details.successful
          aggregatedDetails!.failed += details.failed
          aggregatedDetails!.skipped += details.skipped
        })
      }
      
      return {
        ...prev,
        batchDetails: aggregatedDetails,
        activeBatches: newActiveBatches,
        isProcessing: newActiveBatches.size > 0 || prev.processingCount > 0,
        processingCount: newActiveBatches.size > 0 ? prev.processingCount : Math.max(0, prev.processingCount - 1)
      }
    })
  }

  // Save to localStorage whenever cvProcessing changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        // Convert Map to plain object for localStorage
        const serializable = {
          ...cvProcessing,
          activeBatches: cvProcessing.activeBatches 
            ? Object.fromEntries(cvProcessing.activeBatches)
            : undefined
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable))
      } catch (error) {
        console.error('Error saving CV processing status to localStorage:', error)
      }
    }
  }, [cvProcessing])

  // Poll for CV processing status updates
  useEffect(() => {
    if (cvProcessing.isProcessing) {
      console.log('🔍 [CVProcessing] Starting polling for processing status...')
      const interval = setInterval(async () => {
        try {
          console.log('🔍 [CVProcessing] Polling API for processing status...')
          // Check if any CV uploads are still being processed
          const response = await uploadsApi.getProcessingStatus()
          const data = response.data
          console.log('🔍 [CVProcessing] API response:', data)
          if (!data.hasActiveProcessing && cvProcessing.processingCount > 0) {
            console.log('✅ [CVProcessing] No active processing detected, finishing...')
            finishProcessing({ showDoneMessage: true })
          } else {
            console.log('⏳ [CVProcessing] Still processing, continuing to poll...')
          }
        } catch (error) {
          // If API is not available, use timeout fallback
          console.log('❌ [CVProcessing] Unable to fetch processing status:', error)
          const status = (error as any)?.response?.status
          if (status === 401) {
            // On 401, silently stop polling and clear processing state
            // Don't show error message as it's likely a temporary auth issue
            // The user will be redirected to login if needed
            console.log('🔒 [CVProcessing] Authentication error, stopping polling')
            setCvProcessing(prev => ({
              ...prev,
              isProcessing: false,
              processingCount: 0,
              lastError: null,
              statusMessage: undefined,
              batchDetails: undefined,
              activeBatches: undefined
            }))
          }
        }
      }, 5000) // Check every 5 seconds

      return () => {
        console.log('🛑 [CVProcessing] Stopping polling interval')
        clearInterval(interval)
      }
    }
  }, [cvProcessing.isProcessing, cvProcessing.processingCount])

  return (
    <CVProcessingContext.Provider value={{
      cvProcessing,
      startProcessing,
      finishProcessing,
      setProcessingCount,
      reportError,
      setStatusMessage,
      removeBatch
    }}>
      {children}
    </CVProcessingContext.Provider>
  )
}

export function useCVProcessing() {
  const context = useContext(CVProcessingContext)
  if (context === undefined) {
    throw new Error('useCVProcessing must be used within a CVProcessingProvider')
  }
  return context
}
